import { createFileRoute, Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { Seo } from '../-components/seo';
import { Button } from '@worldcoo/ui/components/button';
import { Layout, Paragraph, Title } from './-components/layout';
import { Header } from './-components/header';
import { Footer } from './-components/footer';
import { Carousel, CarouselItem } from '@worldcoo/ui/components/carousel';
import { Image } from '../-components/image';
import { QuoteIcon } from './-components/icons';
import { currentWindowKey } from '@/lib/data';
import { trpc } from '@/lib/trpc';
import { formatNumber, formatCompactNumber } from '@worldcoo/ui/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { useSuspenseQuery } from '@tanstack/react-query';
import { AnimatedTotalRaised } from './-components/animated-total-raised';
import { Suspense, useState, useEffect, lazy } from 'react';
import { RevealOnScroll } from '@worldcoo/ui/components/reveal-on-scroll';
const Newsletter = lazy(() => import('./-components/newsletter').then((m) => ({ default: m.Newsletter })));

export const Route = createFileRoute('/www/')({
	loader: ({ context }) => {
		context.queryClient.ensureQueryData(trpc.portal.getWorldcooGlobalStats.queryOptions());
	},
	component: Component,
});

function Component() {
	const { t } = useLingui();
	const { language } = useTranslation();
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());

	const staticTotalRaised = formatNumber(language, stats.totalRaised.amount);

	const formattedStoresCount = formatNumber(language, 5000, 0);

	const formattedDonationsCount = formatCompactNumber(language, stats.totalDonations);

	const formattedSocialOrgs = formatNumber(language, stats.socialOrganizations, 0);

	const eroskiStats = stats.featuredMerchants.find((m) => m.portalId === 'eroski');
	const correosStats = stats.featuredMerchants.find((m) => m.portalId === 'correos');
	const ticketmasterStats = stats.featuredMerchants.find((m) => m.portalId === 'ticketmaster');

	const eroskiRaised = eroskiStats
		? formatCompactNumber(language, eroskiStats.totalRaised.amount, { suffix: '€' })
		: '+7M €';
	const eroskiProjects = eroskiStats?.projectsFunded ?? 68;

	const correosRaised = correosStats
		? formatCompactNumber(language, correosStats.totalRaised.amount, { suffix: '€', decimalDigits: 1 })
		: '+1,1M €';
	const correosProjects = correosStats?.projectsFunded ?? 22;

	const ticketmasterRaised = ticketmasterStats
		? formatCompactNumber(language, ticketmasterStats.totalRaised.amount, { suffix: '€' })
		: '+432K €';
	const ticketmasterProjects = ticketmasterStats?.projectsFunded ?? 55;

	const testimonials = [
		{
			author: 'Elena Fernández Rodríguez',
			position: t`Deputy Director of Sustainability and International Relations at Correos.`,
			quote: t`Thanks to "Rounding Up with Impact" we can support a wide variety of projects and collaborate with both well-known NGOs and smaller organizations. Worldcoo has created a unique platform that connects businesses, customers, and social entities, allowing us to act as a true springboard for boosting and scaling initiatives with a positive and tangible social impact.`,
			image: '/home/testimonials/correos.png',
			aspectRatio: 492 / 466,
			alt: 'Correos',
		},
		{
			author: 'Alejandro Martínez',
			position: t`Health and Sustainability Director at Eroski Group`,
			quote: t`An effective tool for our customers to easily channel their commitment to social causes in different areas, helping the most vulnerable groups.`,
			image: '/home/testimonials/eroski.svg',
			aspectRatio: 101 / 21,
			alt: 'Eroski',
		},
		{
			author: 'Manel Prat',
			position: t`Marketing & PR Director at Bonpreu Esclat Group`,
			quote: t`Very happy with the Charity Rounding Up in recent months, as our customers donate easily and regularly. Collaborating with Worldcoo fits perfectly with our DNA and we will work together for many more years.`,
			image: '/home/testimonials/bonpreu-esclat.png',
			aspectRatio: 1140 / 208,
			alt: 'Bonpreu Esclat',
		},
		{
			author: 'Pau Gasol',
			position: t`NBA Player and Co-Founder of the Gasol Foundation`,
			quote: t`A very interesting and encouraging format. Instead of a fixed monthly fee, it gives the user complete freedom to contribute to a social project whenever they want. It also democratizes participation, with amounts accessible to everyone.`,
			image: '/home/testimonials/gasol-foundation.svg',
			aspectRatio: 81 / 33,
			alt: 'Gasol Foundation',
		},
		{
			author: 'Cristina Maragall',
			position: t`Spokesperson for the Pasqual Maragall Foundation`,
			quote: t`A very good initiative because, with little effort, a person feels they are contributing to a good cause. It also says a lot about the values of the company and the people who collaborate.`,
			image: '/home/testimonials/fundacion-pasqual-maragall.svg',
			aspectRatio: 84 / 43,
			alt: 'Fundación Pasqual Maragall',
		},
		{
			author: 'Marta García',
			position: t`Innovation and Customer Satisfaction Director at Viena`,
			quote: t`We are very happy with Worldcoo because it has allowed us to take a step further in our social commitment, helping different organizations. Charity Rounding Up is an accessible and universal way of collaboration with excellent results and great impact.`,
			image: '/home/testimonials/viena.svg',
			aspectRatio: 105 / 29,
			alt: 'Viena',
		},
		{
			author: 'Arantxa Rubio',
			position: t`Communications Director at Muy Mucho`,
			quote: t`With Charity Rounding Up we create community, share values with customers and help Doctors Without Borders in a simple and transparent way. Worldcoo has been the ideal partner to lead it, controlling and spreading the milestones achieved.`,
			image: '/home/testimonials/muymucho.svg',
			aspectRatio: 132 / 30,
			alt: 'Muy Mucho',
		},
		{
			author: 'Jaime Gregori',
			position: t`Director of Fundraising, corporate alliances and social responsibility at Spanish Red Cross`,
			quote: t`With Charity Rounding Up we have been able to give advice for emotional well-being and psychosocial support to people in vulnerable situations affected by Covid.`,
			image: '/home/testimonials/cruz-roja.svg',
			aspectRatio: 126 / 29,
			alt: 'Cruz Roja',
		},
		{
			author: 'Cristina Jover',
			position: t`Communications Manager at PcComponentes`,
			quote: t`It is gratifying to be able to react quickly to emergencies and realize that, with everyone's help, great things can be achieved.`,
			image: '/home/testimonials/pc-componentes.svg',
			aspectRatio: 108 / 38,
			alt: 'PcComponentes',
		},
		{
			author: 'Sara Arcas',
			position: t`Marketing Manager at Wala`,
			quote: t`It modernizes and simplifies fundraising at points of sale. And in sports and fashion, people increasingly value committed companies that give back to society.`,
			image: '/home/testimonials/wala.svg',
			aspectRatio: 61 / 52,
			alt: 'Wala',
		},
	];

	return (
		<Layout>
			<Header />
			<Seo
				title={t`Worldcoo`}
				description={t`An innovative solution to make small donations when paying by card directly from the store's payment terminal`}
				siteName="Worldcoo"
			/>
			{/* Organization JSON-LD */}
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{
					__html: JSON.stringify({
						'@context': 'https://schema.org',
						'@type': 'Organization',
						'name': 'Worldcoo',
						'url': 'https://www.worldcoo.com',
						'description': t`An innovative solution to make small donations when paying by card directly from the store's payment terminal`,
						'logo': 'https://www.worldcoo.com/worldcoo-logo.svg',
					}),
				}}
			/>
			{/* Review JSON-LD for testimonials */}
			{testimonials.map((testimonial) => (
				<script
					key={testimonial.alt}
					type="application/ld+json"
					dangerouslySetInnerHTML={{
						__html: JSON.stringify({
							'@context': 'https://schema.org',
							'@type': 'Review',
							'author': {
								'@type': 'Person',
								'name': testimonial.author,
								'jobTitle': testimonial.position,
							},
							'reviewBody': testimonial.quote,
							'itemReviewed': {
								'@type': 'Organization',
								'name': 'Worldcoo',
							},
						}),
					}}
				/>
			))}

			{/* Hero Section */}
			<section className="bg-hero flex flex-col gap-8">
				<div className="mx-auto flex max-w-7xl flex-col items-center gap-16 p-8 md:flex-row md:py-36">
					<div className="flex flex-col gap-8 md:w-3/5">
						<Title>{t`We transform the way of shopping to build a better world`}</Title>
						<Paragraph>
							{t`Through Charity Rounding Up, customers can make a small donation when paying by card quickly and transparently.`}
						</Paragraph>
						<div className="flex">
							<Button size="lg" asChild>
								<Link to="/www/contact">{t`Activate it and fundraise`}</Link>
							</Button>
						</div>
					</div>

					<div className="items-center justify-end md:flex md:w-2/5">
						<div className="bg-secondary-hero rounded-lg p-8 shadow-xl">
							<h2 className="text-primary text-center text-4xl font-bold">
								<Suspense fallback={<>{staticTotalRaised} €</>}>
									<SuspensedTotalRaised />
								</Suspense>
							</h2>
							<p className="mt-2 text-center text-lg">{t`Raised thanks to Charity Rounding Up`}</p>
						</div>
					</div>
				</div>
			</section>

			{/* Charity Rounding Up Section */}
			<section className="flex flex-col gap-8 bg-white p-8 py-16">
				<div className="mx-auto flex max-w-7xl flex-col gap-16 md:flex-row">
					<div className="w-full md:w-1/2">
						<Image
							src="/worldcoo-terminal-sm.png"
							height={502.78}
							aspectRatio={300 / 502.78}
							priority
							alt={t`A payment terminal displaying the Worldcoo Charity Rounding Up donation option`}
							className="mx-auto"
						/>
					</div>
					<div className="flex w-full flex-col gap-6 md:w-1/2">
						<h2 className="text-primary text-lg font-semibold uppercase">{t`CHARITY ROUNDING UP`}</h2>
						<h3 className="text-4xl font-semibold text-balance md:text-5xl">{t`Micro donations that add up`}</h3>
						<p className="text-base">
							{t`An innovative solution to make small donations when paying by card directly from the store's payment terminal.`}
						</p>

						<div className="mt-4 flex flex-col gap-8 md:flex-row">
							<div className="w-full md:w-1/2">
								<h3 className="text-3xl font-bold">+{formattedStoresCount}</h3>
								<p className="text-base">{t`Stores offer Charity Rounding Up`}</p>
							</div>
							<div className="w-full md:w-1/2">
								<h3 className="text-3xl font-bold">{formattedDonationsCount}</h3>
								<p className="text-base">{t`Donations received`}</p>
							</div>
						</div>

						<div className="mt-4">
							<Button size="lg" asChild>
								<Link to="/www/round-up">{t`Discover more`}</Link>
							</Button>
						</div>
					</div>
				</div>
			</section>

			{/* Testimonials Section */}
			<section className="mx-auto flex max-w-7xl flex-col gap-8 p-8 py-16">
				<TestimonialsCarousel title={t`WHAT THEY SAY ABOUT WORLDCOO`} testimonials={testimonials} />
			</section>

			{/* Success Cases Section */}
			<section className="flex flex-col gap-8 p-8 py-16">
				<div className="mx-auto flex max-w-7xl flex-col items-center gap-6">
					<p className="text-primary text-lg font-semibold uppercase">{t`SUCCESS CASES`}</p>
					<h2 className="text-center text-5xl font-semibold">{t`Companies with impact`}</h2>
				</div>
			</section>

			{/* Eroski */}
			<section className="flex flex-col gap-8 p-8 py-16">
				<div className="mx-auto flex max-w-7xl flex-col gap-16 md:flex-row">
					<div className="w-full md:w-1/2">
						<Image
							src="/home/cases/eroski-case.png"
							alt="Eroski"
							height={460}
							aspectRatio={630 / 460}
							className="w-full rounded-lg shadow-md"
						/>
					</div>
					<div className="flex w-full flex-col gap-6 md:w-1/2">
						<div>
							<h3 className="text-xl font-bold">Eroski</h3>
							<p className="text-base">{t`National supermarket chain`}</p>
						</div>
						<p className="text-base">
							{t`Through Céntimos Solidarios, a permanent plan to support different social causes, their customers can add a few cents when checking out.`}
						</p>

						<div className="mt-4 grid grid-cols-1 gap-8 md:grid-cols-3">
							<div>
								<h4 className="text-2xl font-bold">+1300</h4>
								<p className="text-base">{t`Points of sale`}</p>
							</div>
							<div>
								<h4 className="text-2xl font-bold">{eroskiRaised}</h4>
								<p className="text-base">{t`Raised`}</p>
							</div>
							<div>
								<h4 className="text-2xl font-bold">{eroskiProjects}</h4>
								<p className="text-base">{t`Projects funded`}</p>
							</div>
						</div>

						<div className="mt-4">
							<Button size="lg" asChild>
								<a href="https://eroski.worldcoo.com/" target="_blank" rel="noopener noreferrer">
									{t`Discover more`}
								</a>
							</Button>
						</div>
					</div>
				</div>
			</section>

			{/* Correos */}
			<section className="flex flex-col gap-8 p-8 py-16">
				<div className="mx-auto flex max-w-7xl flex-col gap-16 md:flex-row">
					<div className="order-2 flex w-full flex-col gap-6 md:order-1 md:w-1/2">
						<div>
							<h3 className="text-xl font-bold">Correos</h3>
							<p className="text-base">{t`National Post Office`}</p>
						</div>
						<p className="text-base">
							{t`The Rounding with Impact program offers customers of the office network the possibility to round up the final amount of their purchase made with a bank card, to collaborate in different projects aimed at generating a positive social and/or environmental impact.`}
						</p>

						<div className="mt-4 grid grid-cols-1 gap-8 md:grid-cols-3">
							<div>
								<h4 className="text-2xl font-bold">+2300</h4>
								<p className="text-base">{t`Offices`}</p>
							</div>
							<div>
								<h4 className="text-2xl font-bold">{correosRaised}</h4>
								<p className="text-base">{t`Raised`}</p>
							</div>
							<div>
								<h4 className="text-2xl font-bold">{correosProjects}</h4>
								<p className="text-base">{t`Projects funded`}</p>
							</div>
						</div>

						<div className="mt-4">
							<Button size="lg" asChild>
								<a href="https://correos.worldcoo.com/" target="_blank" rel="noopener noreferrer">
									{t`Discover more`}
								</a>
							</Button>
						</div>
					</div>
					<div className="order-1 w-full md:order-2 md:w-1/2">
						<Image
							src="/home/cases/correos-case.png"
							alt="Correos"
							height={438}
							aspectRatio={600 / 438}
							className="w-full rounded-lg shadow-md"
						/>
					</div>
				</div>
			</section>

			{/* Ticketmaster */}
			<section className="flex flex-col gap-8 p-8 py-16">
				<div className="mx-auto flex max-w-7xl flex-col gap-16 md:flex-row">
					<div className="w-full md:w-1/2">
						<Image
							src="/home/cases/ticketmaster-case.png"
							alt="Ticketmaster"
							height={460}
							aspectRatio={630 / 460}
							className="w-full rounded-lg shadow-md"
						/>
					</div>
					<div className="flex w-full flex-col gap-6 md:w-1/2">
						<div>
							<h3 className="text-xl font-bold">Ticketmaster</h3>
							<p className="text-base">{t`Global event ticket sales company`}</p>
						</div>
						<p className="text-base">
							{t`For 7 years, Ticketmaster customers have raised funds for various social and environmental projects by adding a €1 solidarity contribution when buying their tickets online.`}
						</p>

						<div className="mt-4 grid grid-cols-1 gap-8 md:grid-cols-2">
							<div>
								<h4 className="text-2xl font-bold">{ticketmasterProjects}</h4>
								<p className="text-base">{t`Projects funded`}</p>
							</div>
							<div>
								<h4 className="text-2xl font-bold">{ticketmasterRaised}</h4>
								<p className="text-base">{t`Raised`}</p>
							</div>
						</div>

						<div className="mt-4">
							<Button size="lg" asChild>
								<a href="https://ticketmaster.worldcoo.com/" target="_blank" rel="noopener noreferrer">
									{t`Discover more`}
								</a>
							</Button>
						</div>
					</div>
				</div>
			</section>

			{/* Partners Section */}
			{stats.partnerMerchants && (
				<section className="bg-hero flex flex-col gap-8 p-8 py-16">
					<div className="mx-auto max-w-7xl text-center">
						<h2 className="my-12 text-5xl font-semibold">{t`Already using Charity Rounding Up`}</h2>
						<p className="mb-12 text-lg font-semibold">{t`Businesses helping to build a better world`}</p>

						<ul aria-label={t`Partner logos`} className="flex flex-wrap justify-center gap-6">
							{stats.partnerMerchants.map((merchant) =>
								merchant.logo ? (
									<li key={merchant.id} className="h-16 w-28 rounded-lg bg-white p-2">
										<Image
											src={merchant.logo.location}
											alt={merchant.name ?? merchant.id}
											height={48}
											aspectRatio={96 / 48}
											objectFit="contain"
											className="h-full w-full object-contain!"
										/>
									</li>
								) : null,
							)}
						</ul>
					</div>
				</section>
			)}

			{/* Tech Partners Section */}
			<section className="bg-hero flex flex-col gap-8 p-8 py-16">
				<div className="mx-auto max-w-7xl text-center">
					<p className="mb-12 text-lg font-semibold">{t`Our technological allies that make it possible`}</p>

					<ul aria-label={t`Technology partner logos`} className="flex flex-wrap justify-center gap-6">
						{techPartnerLogos.map((item) => (
							<li key={item.src} className="h-16 w-28 rounded-lg bg-white p-2">
								<Image
									src={item.src}
									alt={item.alt}
									height={48}
									aspectRatio={96 / 48}
									objectFit="contain"
									className="h-full w-full object-contain!"
								/>
							</li>
						))}
					</ul>
				</div>
			</section>

			{/* Social Organizations Section */}
			{stats.socialOrgs && (
				<section className="bg-hero flex flex-col gap-8 p-8 py-16">
					<div className="mx-auto max-w-7xl">
						<p className="mb-12 text-center text-lg font-semibold">
							{t`Collaboration with ${formattedSocialOrgs} social organizations`}
						</p>

						<ul aria-label={t`Social organisation logos`} className="flex flex-wrap justify-center gap-6">
							{stats.socialOrgs.map((org) =>
								org.logo ? (
									<li key={org.id} className="h-16 w-28 rounded-lg bg-white p-2">
										<Image
											src={org.logo.location}
											alt={org.name ?? org.id}
											height={48}
											aspectRatio={96 / 48}
											objectFit="contain"
											className="h-full w-full object-contain!"
										/>
									</li>
								) : null,
							)}
						</ul>
					</div>
				</section>
			)}

			<RevealOnScroll>
				<Newsletter className="bg-background" />
			</RevealOnScroll>
			<Footer />
		</Layout>
	);
}

function SuspensedTotalRaised() {
	const { language } = useTranslation();

	// Track the current 5-minute window key (e.g. "2026-02-13T14:10").
	// Server returns 6 min of data → 1 min margin while the next batch loads.
	const [windowKey, setWindowKey] = useState(currentWindowKey);
	useEffect(() => {
		const timer = setInterval(() => {
			const key = currentWindowKey();
			if (key !== windowKey) setWindowKey(key);
		}, 10_000);
		return () => clearInterval(timer);
	}, [windowKey]);

	const { data } = useSuspenseQuery(
		trpc.portal.getRecentDonations.queryOptions({ windowKey, mode: 'day' }, { staleTime: 10 * 60 * 1000 }),
	);

	return (
		<AnimatedTotalRaised
			totalAtWindowStartCents={data.totalAtWindowStartCents}
			donations={data.donations}
			language={language}
		/>
	);
}

function TestimonialsCarousel(props: {
	title: string;
	testimonials: {
		quote: string;
		author: string;
		position: string;
		image: string;
		aspectRatio: number;
		alt: string;
	}[];
}) {
	return (
		<div className="flex flex-col gap-12">
			<h2 className="text-4xl font-bold">{props.title}</h2>

			<div className="w-full">
				<Carousel className="mx-auto max-w-6xl">
					{props.testimonials.map((testimonial) => (
						<CarouselItem key={testimonial.quote} className="md:basis-1/2">
							<figure className="bg-secondary-hero flex h-full flex-col gap-6 rounded-lg p-8 shadow-md transition-all hover:shadow-lg">
								<div className="text-primary mb-2" aria-hidden>
									<QuoteIcon width={46} height={28} />
								</div>
								<blockquote className="grow text-lg">{testimonial.quote}</blockquote>
								<figcaption className="flex items-center justify-between gap-4">
									<div className="mt-auto flex-1 pt-4">
										<p className="font-bold">{testimonial.author}</p>
										<p>{testimonial.position}</p>
									</div>

									<div className="flex min-w-0 flex-1 items-center justify-center">
										<Image
											src={testimonial.image}
											alt={testimonial.alt}
											aspectRatio={testimonial.aspectRatio}
											height={50}
											className="max-w-full object-contain!"
										/>
									</div>
								</figcaption>
							</figure>
						</CarouselItem>
					))}
				</Carousel>
			</div>
		</div>
	);
}

const techPartnerLogos = [
	{ src: '/home/tech-partners/wordline.png', alt: 'Worldline' },
	{ src: '/home/tech-partners/mastercard.png', alt: 'Mastercard' },
	{ src: '/home/tech-partners/diebold-nixford.png', alt: 'Diebold Nixford' },
	{ src: '/home/tech-partners/clearone.png', alt: 'ClearOne' },
	{ src: '/home/tech-partners/global-payments.png', alt: 'Global Payments' },
	{ src: '/home/tech-partners/ingenico-group.png', alt: 'Ingenico Group' },
	{ src: '/home/tech-partners/saima-systems.png', alt: 'Saima Systems' },
	{ src: '/home/tech-partners/ecopaynet.png', alt: 'Ecopaynet' },
	{ src: '/home/tech-partners/necomplus.png', alt: 'Necomplus' },
	{ src: '/home/tech-partners/sabadell.png', alt: 'Sabadell' },
	{ src: '/home/tech-partners/comercia-global-payments.png', alt: 'Comercia Global Payments' },
	{ src: '/home/tech-partners/sistemas-avanzados-en-monetica.png', alt: 'Sistemas Avanzados en Monetica' },
	{ src: '/home/tech-partners/sipay.png', alt: 'Sipay' },
	{ src: '/home/tech-partners/paytef.png', alt: 'Paytef' },
	{ src: '/home/tech-partners/adyen.png', alt: 'Adyen' },
	{ src: '/home/tech-partners/ncr.png', alt: 'NCR' },
];
